import {
    useActivitiesInstancesMap,
    useAssessmentsInstancesQuery,
    useTemplatesMap,
} from "@Components/assessment/queries";
import { IExtendedAssessmentInstanceDetails, IUseAssessmentData } from "@Components/assessment/types";
import SessionManager from "@Core/session";
import { IActivityDetails } from "@Models/IActivityDetails";
import { IAssessmentInstanceDetails } from "@Models/IAssessmentDetails";
import { IFeedbackInstanceDetails } from "@Models/IFeedbackInstanceDetails";
import { IFeedbackTemplateDetails } from "@Models/IFeedbackTemplateDetails";
import { ITaskSubmissionDetails } from "@Models/ITaskDetails";
import { useSearchParams } from "@Utils";
import { useState } from "react";
import { extendAssessmentInstanceDetails } from "./../helpers";
import { useCompleteAssessmentInstance } from "./../mutations";
import { IKnownDictionary } from "@Components/shared/types";

interface IUseAdminSubmissionsData extends IUseAssessmentData<IAssessmentInstanceDetails, ITaskSubmissionDetails> {
    data: {
        activityInstance: {
            isLoading: boolean;
            onSelect: (activityInstanceId: ITaskSubmissionDetails["activityInstanceId"]) => void;
            videoActivity: IActivityDetails;
        };
        assessmentInstanceId: {
            isLoading: boolean;
            list: IAssessmentInstanceDetails[];
            onSelect: (assessmentInstanceId: IAssessmentInstanceDetails["assessmentInstanceId"]) => void;
        };
    };
    template: IFeedbackTemplateDetails;
    submission: IExtendedAssessmentInstanceDetails;

    onSubmit: (values: IKnownDictionary) => void;
}

const useAdminSubmissions = (): IUseAdminSubmissionsData => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [videoActivity, setVideoActivity] = useState<IActivityDetails>();
    const { fullName, userId } = SessionManager.getServiceUser();

    const assessmentInstanceParam: string = searchParams.get("assessmentInstance");
    const assessmentParam: string = searchParams.get("assessment");
    const tabParam: string = searchParams.get("tab");

    //  the tab is only active when at least one assessment instance is in progress

    const {
        data: assessmentInstances,
        isFetched: areAssessmentInstancesFetched,
        isFetching: areAssessmentInstancesFetching,
    } = useAssessmentsInstancesQuery(assessmentParam);

    const progressedAssessmentInstances = assessmentInstances?.filter((ai) => ai.status !== "assigned");

    // ensure only a progress assessment instance is selected by default
    const assessmentInstanceId = assessmentInstanceParam || progressedAssessmentInstances?.[0]?.assessmentInstanceId;
    const assessmentInstance = assessmentInstances.find((ai) => ai.assessmentInstanceId === assessmentInstanceId);

    const { data: templatesMap, isFetched: isTemplatesMapFetched } = useTemplatesMap();
    const { mutate: completeAssessment, isLoading: isSubmitting } = useCompleteAssessmentInstance(assessmentParam);

    const activityInstanceIds = assessmentInstance?.tasks?.map((td) => td.activityInstanceId) || [];

    const {
        data: activitiesInstancesMap,
        isFetched: isActivitiesInstancesMapFetched,
        isFetching: isActivitiesInstancesMapFetching,
    } = useActivitiesInstancesMap(activityInstanceIds);

    const handleAssessmentInstanceSelect = (id: IAssessmentInstanceDetails["assessmentInstanceId"]) =>
        setSearchParams({ tab: tabParam, assessment: assessmentParam, assessmentInstance: id });

    const handleActivityInstanceSelect = (activityInstanceId: ITaskSubmissionDetails["activityInstanceId"]) =>
        setVideoActivity(activitiesInstancesMap.get(activityInstanceId));

    const handleSubmit = (values: IKnownDictionary) => {
        const formData: IFeedbackInstanceDetails["formData"] = Object.entries(values).map(([id, value]) => ({
            id: String(id),
            value: String(value),
        }));

        const model: { model: IFeedbackInstanceDetails } = {
            model: {
                assessmentInstanceId: submission.assessmentInstanceId,
                authorFullName: fullName,
                authorId: userId,
                formData,
                submissionDate: submission.submissionDate, // confirm this is needed
                templateId: assessmentInstance.feedbackTemplateId,
            },
        };

        completeAssessment(model, {
            onSuccess: () => {
                setSearchParams({ assessment: assessmentParam, tab: "users" });
            },
        });
    };

    const template = templatesMap.get(assessmentInstance?.feedbackTemplateId);

    const isEverythingFetched =
        areAssessmentInstancesFetched && isTemplatesMapFetched && isActivitiesInstancesMapFetched;

    const submission =
        isEverythingFetched && template
            ? extendAssessmentInstanceDetails(assessmentInstance, "", activitiesInstancesMap)
            : null;

    return {
        data: {
            activityInstance: {
                isLoading: !isEverythingFetched || isActivitiesInstancesMapFetching,
                onSelect: handleActivityInstanceSelect,
                videoActivity,
            },
            assessmentInstanceId: {
                isLoading: !isEverythingFetched || areAssessmentInstancesFetching,
                list: assessmentInstances,
                onSelect: handleAssessmentInstanceSelect,
            },
        },
        isDisabled: userId !== assessmentInstance?.owner || isSubmitting,
        isLoading: !isEverythingFetched,
        isSubmitting,
        template,
        onSubmit: handleSubmit,
        submission,
    };
};

export default useAdminSubmissions;
